import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, Trash2 } from "lucide-react";
import { API } from "../../App";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

const blank = () => ({ id: "", question: "", choices: ["", "", "", ""], answer_index: 0, explanation: "" });

const QuestionEditor = ({ q, i, onChange }) => {
  const set = (patch) => onChange({ ...q, ...patch });
  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid={`quiz-editor-question-${i}`}>
      <Textarea rows={2} value={q.question} placeholder={`Question ${i + 1}`} onChange={(e) => set({ question: e.target.value })} data-testid={`quiz-editor-q-${i}`} />
      {q.choices.map((c, ci) => (
        <label key={ci} className="flex items-center gap-2">
          <input type="radio" name={`correct-${i}`} checked={q.answer_index === ci} onChange={() => set({ answer_index: ci })} aria-label={`Mark choice ${ci + 1} correct`} data-testid={`quiz-editor-correct-${i}-${ci}`} />
          <Input value={c} placeholder={`Choice ${ci + 1}`} className={q.answer_index === ci ? "border-green-500" : ""} onChange={(e) => set({ choices: q.choices.map((x, xi) => (xi === ci ? e.target.value : x)) })} data-testid={`quiz-editor-choice-${i}-${ci}`} />
        </label>
      ))}
      <Input value={q.explanation} placeholder="Why the answer is right (shown after passing)" onChange={(e) => set({ explanation: e.target.value })} data-testid={`quiz-editor-expl-${i}`} />
    </div>
  );
};

export const QuizEditorDialog = ({ song, token, onClose, onSaved }) => {
  const [quiz, setQuiz] = useState(null);
  const [busy, setBusy] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const base = song ? `${API}/admin/quizzes/${song.content_id}` : "";

  useEffect(() => {
    if (!song) return;
    setQuiz(null);
    axios.get(base, { headers }).then((r) => setQuiz(r.data.questions?.length ? r.data : { ...r.data, questions: Array.from({ length: 5 }, blank) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song?.content_id]);

  const act = async (kind, fn, ok) => {
    setBusy(kind);
    try {
      const r = await fn();
      if (r?.data?.questions) setQuiz(r.data);
      toast.success(ok);
      onSaved();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const save = (status) => act(status, () => axios.put(base, { questions: quiz.questions, status }, { headers }), status === "published" ? "Quiz published: students can take it now" : "Saved as draft");
  const generate = () => {
    if (quiz?.status === "published" && !window.confirm("This quiz is live. Redrafting replaces its questions and takes it offline until you publish again. Continue?")) return;
    act("ai", () => axios.post(`${base}/generate`, {}, { headers }), "AI draft ready: please review every question");
  };

  return (
    <Dialog open={!!song} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="quiz-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">Quiz: {song?.title}</DialogTitle>
          <DialogDescription>
            {quiz?.status === "published" ? "Live for students." : "Not visible to students until you publish."} Select the circle next to the correct answer.
          </DialogDescription>
        </DialogHeader>
        {!quiz ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-gold" /> : (
          <div className="space-y-4">
            <Button variant="outline" size="sm" disabled={!!busy} onClick={generate} data-testid="quiz-editor-generate-btn">
              {busy === "ai" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              {busy === "ai" ? "Drafting (about 20 seconds)…" : quiz.source === "ai" || quiz.status !== "none" ? "Redraft with AI" : "Draft with AI"}
            </Button>
            {quiz.questions.map((q, i) => (
              <QuestionEditor key={i} q={q} i={i} onChange={(nq) => setQuiz({ ...quiz, questions: quiz.questions.map((x, xi) => (xi === i ? nq : x)) })} />
            ))}
            <div className="flex flex-wrap gap-2 border-t pt-4">
              <Button disabled={!!busy} onClick={() => save("published")} data-testid="quiz-editor-publish-btn">{busy === "published" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Approve & publish</Button>
              <Button variant="outline" disabled={!!busy} onClick={() => save("draft")} data-testid="quiz-editor-save-draft-btn">{quiz.status === "published" ? "Unpublish (save as draft)" : "Save draft"}</Button>
              {quiz.status !== "none" && (
                <Button variant="ghost" className="ml-auto text-destructive" disabled={!!busy} onClick={() => window.confirm("Delete this quiz?") && act("del", () => axios.delete(base, { headers }), "Quiz deleted").then((ok) => ok && onClose())} data-testid="quiz-editor-delete-btn">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
